import { z } from 'zod'
import { PERMISSIONS } from './permissions.js'
import { DECLINE_REASON_KEYS } from './proposal.js'
import { SITE_ROLE_KEYS } from './site-people.js'

/** Shared field-level rules, so the same constraint is not written twice. */
export const emailSchema = z.string().trim().toLowerCase().email().max(320)

/**
 * Password rules deliberately favour length over character classes.
 *
 * Composition requirements push people towards `Password1!` and a sticky
 * note. A twelve-character minimum with no other rules is both easier to
 * comply with and harder to guess.
 */
export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters.')
  .max(200, 'That is longer than necessary.')

export const cuidSchema = z.string().min(1).max(64)

type Patched<T extends z.ZodRawShape> = {
  [K in keyof T]: z.ZodOptional<T[K] extends z.ZodDefault<infer Inner> ? Inner : T[K]>
}

/**
 * Every field optional, and no default filled in for the ones left out.
 *
 * This is what an edit route takes, and `schema.partial()` is not it under
 * Zod 4: a field declared `.default(x)` stays defaulted inside the optional, so
 * `{ id, categoryId }` reached `jobs.update` as `{ id, categoryId, status:
 * 'LEAD', priority: 'NORMAL' }` and recategorising a job reopened it as a lead,
 * and retitling a draft quote sent `lines: []` and deleted every line. An
 * update writes what was sent and nothing else.
 */
export function patchSchema<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<Patched<T>> {
  const shape: Record<string, z.ZodType> = {}
  for (const [key, field] of Object.entries(schema.shape)) {
    const inner = field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : (field as z.ZodType)
    shape[key] = inner.optional()
  }
  return z.object(shape) as unknown as z.ZodObject<Patched<T>>
}

/**
 * An optional text field that a form clears by submitting an empty box.
 *
 * `""` and `null` both mean "not set", and letting both into the column makes
 * every reader check for two things. The empty string is folded here, once,
 * rather than in each mutation.
 */
function blankToNull<T extends z.ZodType<string>>(inner: T) {
  return z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    inner.nullish(),
  )
}

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password.').max(200),
  /** Names the session so a lost handset can be revoked on its own. */
  deviceName: z.string().trim().max(80).optional(),
})

export const requestPasswordResetSchema = z.object({ email: emailSchema })

export const resetPasswordSchema = z.object({
  token: z.string().min(1).max(200),
  password: passwordSchema,
})

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
})

export const inviteUserSchema = z.object({
  email: emailSchema,
  name: z.string().trim().min(1).max(120),
  roleId: cuidSchema,
  branchId: cuidSchema.optional(),
})

export const acceptInviteSchema = z.object({
  token: z.string().min(1).max(200),
  name: z.string().trim().min(1).max(120).optional(),
  password: passwordSchema,
})

export const updateUserSchema = z.object({
  id: cuidSchema,
  name: z.string().trim().min(1).max(120).optional(),
  phone: z.string().trim().max(40).nullish(),
  colour: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour such as #6aa84f.')
    .optional(),
  roleId: cuidSchema.optional(),
  branchId: cuidSchema.nullish(),
  isSchedulable: z.boolean().optional(),
  costRatePerHour: z.number().int().min(0).nullish(),
  chargeRatePerHour: z.number().int().min(0).nullish(),
  extraPermissions: z.array(z.enum(PERMISSIONS)).optional(),
  revokedPermissions: z.array(z.enum(PERMISSIONS)).optional(),
  /** Where their day starts and ends. Null clears it; absent leaves it alone. */
  homeAddress: z.lazy(() => addressSchema).nullish(),
  /** A few words the customer reads on their booking page. Decision 104. */
  bio: z.string().trim().max(600).nullish(),
})

export const roleInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).nullish(),
  permissions: z.array(z.enum(PERMISSIONS)),
})

/**
 * An address, as a form sends one.
 *
 * The coordinates and the place id are accepted from the caller rather than
 * looked up here, because the lookup has already happened: the picker resolved
 * a Google place through `places.details` before anybody pressed Save, and
 * geocoding the same address again on the way to the database would be a
 * second billable call for an answer we are already holding.
 *
 * `geocodeStatus` is deliberately **not** in this schema. It is derived from
 * whether coordinates arrived and where they came from, so a caller cannot
 * claim a precision it did not measure.
 */
export const addressSchema = z.object({
  raw: z.string().trim().max(300).nullish(),
  unit: z.string().trim().max(40).nullish(),
  streetNumber: z.string().trim().max(40).nullish(),
  street: z.string().trim().max(160).nullish(),
  suburb: z.string().trim().max(120).nullish(),
  city: z.string().trim().max(120).nullish(),
  state: z.string().trim().max(60).nullish(),
  postcode: z.string().trim().max(20).nullish(),
  country: z.string().trim().length(2).default('AU'),
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
  placeId: z.string().trim().max(200).nullish(),
})

export type AddressInput = z.infer<typeof addressSchema>

/**
 * What an address is worth, given how it was arrived at — or null when the
 * payload says nothing about where the place is.
 *
 * Null matters more than the two statuses do. A technician correcting a street
 * name sends no coordinates, and stamping that write `NOT_ATTEMPTED` would
 * throw away a geocode nobody asked to discard. So the caller leaves
 * `geocodeStatus` alone unless this returns something.
 *
 * `EXACT` is reserved for a place the picker resolved: Google named the
 * building and returned its point. Coordinates arriving without a place id are
 * `MANUAL`, because `APPROXIMATE` already means something specific here —
 * ServiceM8's own geocode, carried in by the import.
 */
export function geocodeStatusFor(
  address: Pick<AddressInput, 'latitude' | 'longitude' | 'placeId'>,
): 'EXACT' | 'MANUAL' | null {
  if (address.latitude == null || address.longitude == null) return null
  return address.placeId ? 'EXACT' : 'MANUAL'
}

export const clientInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  type: z.enum(['RESIDENTIAL', 'COMMERCIAL', 'STRATA', 'GOVERNMENT', 'REAL_ESTATE']).default('RESIDENTIAL'),
  email: emailSchema.nullish(),
  phone: z.string().trim().max(40).nullish(),
  website: z.string().trim().max(200).nullish(),
  taxNumber: z.string().trim().max(40).nullish(),
  paymentTermsDays: z.number().int().min(0).max(365).default(14),
  creditLimit: z.number().int().min(0).nullish(),
  isOnCreditHold: z.boolean().default(false),
  priceTierId: cuidSchema.nullish(),
  taxExempt: z.boolean().default(false),
  notes: z.string().trim().max(5000).nullish(),
  billingAddress: addressSchema.nullish(),
})

/**
 * A person at a client.
 *
 * `Contact.firstName` is the only required part because that is all the office
 * has when a job comes in from a mobile number and a first name.
 */
export const contactInputSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: blankToNull(z.string().trim().max(80)),
  position: blankToNull(z.string().trim().max(80)),
  email: blankToNull(emailSchema),
  phone: blankToNull(z.string().trim().max(40)),
  mobile: blankToNull(z.string().trim().max(40)),
  /** What a person at a site is there. Ignored for the client's own people. Decision 126. */
  siteRole: z.enum(SITE_ROLE_KEYS).nullish(),
})

/**
 * Creating a client, with the first person at it.
 *
 * An individual and a business are the same record: the only difference is
 * whether `name` holds a person or a company, and both carry contacts either
 * way. Making the contact part of the create is what stops "Tom Smith" being
 * a client with nobody to ring. Decision 60.
 */
export const clientCreateSchema = clientInputSchema.extend({
  primaryContact: contactInputSchema.nullish(),
})

/**
 * What the property is like to arrive at.
 *
 * Split out of `siteInputSchema` because it is written from a different
 * screen by a different person — a technician correcting "there is a dog"
 * from the job card, rather than the office creating a site — and because
 * writing any of it stamps `knowledgeReviewedAt`, which the client never
 * sends. Nullable throughout: null is "nobody has said", which is not the
 * same answer as false. Decision 41.
 */
export const siteKnowledgeSchema = z.object({
  accessNotes: z.string().trim().max(5000).nullish(),
  hazardNotes: z.string().trim().max(5000).nullish(),
  gateCode: z.string().trim().max(60).nullish(),
  hasPets: z.boolean().nullish(),
  hasChildren: z.boolean().nullish(),
  accessMethod: z
    .enum(['CLIENT_PRESENT', 'KEY_SAFE', 'GATE_CODE', 'AGENT_KEY', 'OPEN_ACCESS', 'OTHER'])
    .nullish(),
  parkingNotes: z.string().trim().max(2000).nullish(),
})

export const siteInputSchema = siteKnowledgeSchema.extend({
  clientId: cuidSchema,
  name: z.string().trim().max(160).nullish(),
  address: addressSchema,
  parentSiteId: cuidSchema.nullish(),
})

/** The technician's correction, from the job card. */
export const updateSiteKnowledgeSchema = siteKnowledgeSchema.partial().extend({
  id: cuidSchema,
})

export const jobInputSchema = z.object({
  clientId: cuidSchema,
  siteId: cuidSchema.nullish(),
  branchId: cuidSchema.nullish(),
  categoryId: cuidSchema.nullish(),
  queueId: cuidSchema.nullish(),
  assigneeId: cuidSchema.nullish(),
  assetId: cuidSchema.nullish(),
  status: z
    .enum([
      'LEAD',
      'QUOTING',
      'QUOTED',
      'APPROVED',
      'SCHEDULED',
      'IN_PROGRESS',
      'ON_HOLD',
      'AWAITING_PARTS',
      'COMPLETED',
      'CANCELLED',
    ])
    .default('LEAD'),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'EMERGENCY']).default('NORMAL'),
  title: z.string().trim().max(200).nullish(),
  /**
   * The imported ServiceM8 text, and nothing else.
   *
   * Still writable so an existing job's history can be corrected, but the
   * job card offers no editor for it and nothing new should be written here.
   * The brief below is where a new job says what it is. Decision 41.
   */
  description: z.string().trim().max(20000).nullish(),
  workDone: z.string().trim().max(20000).nullish(),

  // The brief.
  scope: z.string().trim().max(5000).nullish(),
  confirmedWith: z.string().trim().max(160).nullish(),
  confirmedVia: z.enum(['PHONE', 'SMS', 'EMAIL', 'PORTAL', 'IN_PERSON']).nullish(),
  confirmedAt: z.coerce.date().nullish(),
  /** Minor units, like every other money field. Never a float or a string. */
  agreedPriceIncTax: z.number().int().min(0).nullish(),
  paymentCollection: z.enum(['COLLECT_ON_DAY', 'INVOICE']).nullish(),
  reportDelivery: z.enum(['EMAIL_BILLING', 'EMAIL_SITE', 'DO_NOT_SEND']).nullish(),

  source: z.string().trim().max(80).nullish(),
  purchaseOrderNumber: z.string().trim().max(60).nullish(),
  estimatedMinutes: z.number().int().min(0).max(60 * 24 * 30).nullish(),
  dueAt: z.coerce.date().nullish(),
})

/**
 * Creating a job.
 *
 * Separate from `jobInputSchema` only because of `templateId`: a template is
 * something a job is created *from*, not a property of it that can be edited
 * afterwards. Re-applying one to a live job would overwrite a brief somebody
 * has since corrected.
 */
export const createJobSchema = jobInputSchema.extend({
  /**
   * Omit it and the category picks one, when the category has exactly one.
   * Send `null` to create the job with nothing prefilled.
   */
  templateId: cuidSchema.nullish(),
})

export const checklistAudienceSchema = z.enum(['OFFICE', 'TECH', 'BOTH'])
export const checklistItemTypeSchema = z.enum(['TODO', 'PHOTO', 'FORM', 'ASSET'])

export const jobTemplateSchema = z.object({
  id: cuidSchema.optional(),
  name: z.string().trim().min(1).max(120),
  categoryId: cuidSchema.nullish(),
  titleTemplate: z.string().trim().max(200).nullish(),
  scopeTemplate: z.string().trim().max(5000).nullish(),
  defaultPriority: z.enum(['LOW', 'NORMAL', 'HIGH', 'EMERGENCY']).default('NORMAL'),
  estimatedMinutes: z.number().int().min(0).max(60 * 24 * 30).nullish(),
  defaultPriceIncTax: z.number().int().min(0).nullish(),
  paymentCollection: z.enum(['COLLECT_ON_DAY', 'INVOICE']).nullish(),
  reportDelivery: z.enum(['EMAIL_BILLING', 'EMAIL_SITE', 'DO_NOT_SEND']).nullish(),
  defaultRrule: z.string().trim().max(500).nullish(),
  sections: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(120),
        audience: checklistAudienceSchema.default('BOTH'),
        items: z
          .array(
            z.object({
              text: z.string().trim().min(1).max(500),
              itemType: checklistItemTypeSchema.default('TODO'),
              isRequired: z.boolean().default(false),
              formId: cuidSchema.nullish(),
            }),
          )
          .max(200)
          .default([]),
      }),
    )
    .max(40)
    .default([]),
})

/**
 * A booking. `endAt` is validated against `startAt` here rather than only in
 * the database, so the board can refuse an impossible drag without a round
 * trip and still be backed by a constraint if anything else tries.
 */
export const visitInputSchema = z
  .object({
    jobId: cuidSchema,
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    userIds: z.array(cuidSchema).default([]),
    // Booking something is not the same as the customer agreeing to it, so a
    // new booking is a plan until somebody says otherwise. Decision 76.
    status: z
      .enum(['PROPOSED', 'CONFIRMED', 'EN_ROUTE', 'ON_SITE', 'COMPLETED', 'CANCELLED', 'NO_SHOW'])
      .default('PROPOSED'),
    travelMinutes: z.number().int().min(0).max(600).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    isAllDay: z.boolean().default(false),
  })
  .refine((visit) => visit.endAt > visit.startAt, {
    message: 'A visit has to end after it starts.',
    path: ['endAt'],
  })

/** Moving or resizing a block on the board. */
export const moveVisitSchema = z
  .object({
    id: cuidSchema,
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    /** Present when the drag crossed into another person's column. */
    userIds: z.array(cuidSchema).optional(),
  })
  .refine((visit) => visit.endAt > visit.startAt, {
    message: 'A visit has to end after it starts.',
    path: ['endAt'],
  })

/**
 * Moving several blocks at once.
 *
 * A multi-select drag is one gesture, so it is one round trip and one conflict
 * pass. Sending it as N `moveVisit` calls means the board can end up half
 * moved if the fourth one fails, and reports the same clash four times.
 */
export const moveVisitsSchema = z.object({
  moves: z.array(moveVisitSchema).min(1).max(50),
})

/**
 * Peel one person off a shared booking. The original keeps everyone else;
 * the new visit is the same job at the drop time.
 */
export const splitVisitSchema = z
  .object({
    id: cuidSchema,
    userId: cuidSchema,
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    /** The row they landed on, when the drag also changed column. */
    landOnUserId: cuidSchema.optional(),
  })
  .refine((visit) => visit.endAt > visit.startAt, {
    message: 'A visit has to end after it starts.',
    path: ['endAt'],
  })

/**
 * Leave and other non-working time. Deliberately spare: a type, a window and
 * an optional note is everything the board needs to draw someone as
 * unavailable, and anything more (accrual balances, approval workflow) is a
 * different feature that can be layered on without touching this shape.
 */
export const leaveInputSchema = z
  .object({
    userId: cuidSchema,
    type: z.enum(['ANNUAL', 'SICK', 'UNPAID', 'PUBLIC_HOLIDAY', 'OTHER']).default('ANNUAL'),
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    note: z.string().trim().max(500).nullish(),
  })
  .refine((leave) => leave.endAt > leave.startAt, {
    message: 'Leave has to end after it starts.',
    path: ['endAt'],
  })

/**
 * Moving, stretching, handing to someone else or re-wording leave already on
 * the board. Every field but the id is optional; the router checks the order
 * of the times against what is stored, because a resize sends only one edge's
 * worth of change.
 */
export const leaveUpdateSchema = z.object({
  id: cuidSchema,
  userId: cuidSchema.optional(),
  startAt: z.coerce.date().optional(),
  endAt: z.coerce.date().optional(),
  note: z.string().trim().max(500).nullish(),
})

/**
 * Booked time that is not a customer job: a keys stop, a break, a meeting.
 *
 * Attendees can be empty so a stop can sit on "Needs a tech" the way a
 * visit can. Address, phone and job are the TASK fields; they are legal on
 * any type so a meeting at the depot can still name the place.
 */
export const calendarEventTypeSchema = z.enum([
  'MEETING',
  'TRAINING',
  'ADMIN',
  'OTHER',
  'BREAK',
  'TASK',
])

export const calendarEventInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    type: calendarEventTypeSchema.default('MEETING'),
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    note: z.string().trim().max(500).nullish(),
    phone: z.string().trim().max(40).nullish(),
    jobId: cuidSchema.nullish(),
    address: addressSchema.nullish(),
    userIds: z.array(cuidSchema).max(100).default([]),
  })
  .refine((event) => event.endAt > event.startAt, {
    message: 'An event has to end after it starts.',
    path: ['endAt'],
  })

export const calendarEventUpdateSchema = z
  .object({
    id: cuidSchema,
    title: z.string().trim().min(1).max(200).optional(),
    type: calendarEventTypeSchema.optional(),
    startAt: z.coerce.date().optional(),
    endAt: z.coerce.date().optional(),
    note: z.string().trim().max(500).nullish(),
    phone: z.string().trim().max(40).nullish(),
    jobId: cuidSchema.nullish(),
    /** `null` clears the address. Omitted leaves it. */
    address: addressSchema.nullish(),
    userIds: z.array(cuidSchema).max(100).optional(),
  })
  .refine(
    (event) =>
      event.startAt == null || event.endAt == null || event.endAt > event.startAt,
    {
      message: 'An event has to end after it starts.',
      path: ['endAt'],
    },
  )

export const moveEventSchema = z
  .object({
    id: cuidSchema,
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    userIds: z.array(cuidSchema).optional(),
  })
  .refine((event) => event.endAt > event.startAt, {
    message: 'An event has to end after it starts.',
    path: ['endAt'],
  })

/** Renaming or reordering a board section, or a parking bucket. */
export const boardLabelSchema = z.object({
  name: z.string().trim().min(1).max(60),
  colour: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #64748b')
    .default('#64748b'),
  sortOrder: z.number().int().min(0).max(999).default(0),
})

/**
 * Adding or renaming a kind of work.
 *
 * `parentId` is what makes a subcategory: a category with a parent is one, and
 * a job still points at a single row either way. Every field but the name is
 * optional so a rename does not have to resend the colour. Decision 71.
 */
export const jobCategorySchema = z.object({
  id: cuidSchema.optional(),
  name: z.string().trim().min(1).max(120).optional(),
  colour: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #64748b')
    .optional(),
  parentId: cuidSchema.nullish(),
  sortOrder: z.number().int().min(0).max(999).optional(),
})

/**
 * One level of the catalog, in the order it should read.
 *
 * Where a row sits and which group it is in are one gesture — a drag lands it
 * between two rows *inside* something — so they are one write. `orderedIds` is
 * every row that should end up under `parentId`, in order, and everything in it
 * is moved there whether or not it was there before. Sending a partial list
 * would leave the rows that were left out holding stale positions among the
 * ones that moved.
 *
 * `parentId: null` is the top level, where the groups themselves live.
 */
export const reorderCategoriesSchema = z.object({
  parentId: cuidSchema.nullish(),
  orderedIds: z.array(cuidSchema).min(1).max(500),
})

export const lineInputSchema = z.object({
  materialId: cuidSchema.nullish(),
  description: z.string().trim().min(1).max(1000),
  /** Decimal string, not a float, so 0.1 + 0.2 never enters the picture. */
  quantity: z.union([z.number(), z.string()]),
  unitPrice: z.number().int(),
  /**
   * The price as typed inc tax. When present it is what the line charges and
   * `unitPrice` is recomputed from it on the server.
   */
  unitPriceIncTax: z.number().int().nullish(),
  unitCost: z.number().int().default(0),
  discountBasisPoints: z.number().int().min(0).max(10000).default(0),
  taxRateId: cuidSchema.nullish(),
  isOptional: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  /**
   * Which package the line is in, by the `key` of an entry in `options`.
   * Quote lines only; an invoice has no packages and ignores it.
   */
  optionKey: z.string().trim().min(1).max(64).nullish(),
  /** The service this line sells, which acceptance puts on the job. */
  serviceTypeId: cuidSchema.nullish(),
})

export const QUOTE_SECTION_KINDS = ['COVER', 'TEXT', 'IMAGE', 'PRICING', 'TERMS'] as const
/** Photos and PDFs on one proposal block. */
export const MAX_SECTION_FILES = 24
export type QuoteSectionKindKey = (typeof QUOTE_SECTION_KINDS)[number]

/** One block of a proposal. Plain text only — see `proposalBlocks`. */
export const quoteSectionInputSchema = z.object({
  kind: z.enum(QUOTE_SECTION_KINDS),
  title: blankToNull(z.string().trim().max(200)),
  body: blankToNull(z.string().trim().max(20000)),
  /** A cover's photograph. */
  attachmentId: cuidSchema.nullish(),
  /**
   * A `IMAGE` block's photos and PDFs, in the order they print. Read through
   * `sectionFileIds`, which also counts a block saved before this existed.
   */
  fileIds: z.array(cuidSchema).max(MAX_SECTION_FILES).default([]),
})

/**
 * A package. `key` is the client's name for it, which lines point at through
 * `optionKey`; the server swaps it for a real id when it writes the row.
 */
export const quoteOptionInputSchema = z.object({
  key: z.string().trim().min(1).max(64),
  name: z.string().trim().min(1).max(120),
  summary: blankToNull(z.string().trim().max(4000)),
  isRecommended: z.boolean().default(false),
})

export const quoteInputSchema = z.object({
  jobId: cuidSchema,
  title: z.string().trim().max(200).nullish(),
  termsText: z.string().trim().max(20000).nullish(),
  validUntil: z.coerce.date().nullish(),
  discountAmount: z.number().int().min(0).default(0),
  lines: z.array(lineInputSchema).default([]),
  /**
   * A proposal's blocks, in order. Absent on update means "leave them"; an
   * empty list removes them.
   */
  sections: z.array(quoteSectionInputSchema).max(60).optional(),
  /**
   * The packages. Written together with `lines`, because lines name them by
   * key — the server refuses one without the other.
   */
  options: z.array(quoteOptionInputSchema).max(6).optional(),
})

/**
 * An edit to a draft quote: only what was sent is written.
 *
 * Not `quoteInputSchema.partial()`, because zod 4 still applies a default
 * under `.partial()` — an update carrying only a title would arrive with
 * `lines: []` and `discountAmount: 0` and wipe both.
 */
export const quoteUpdateSchema = quoteInputSchema
  .extend({
    discountAmount: z.number().int().min(0).optional(),
    lines: z.array(lineInputSchema).optional(),
  })
  .omit({ jobId: true })
  .partial()
  .extend({ id: z.string() })

export type QuoteSectionInput = z.infer<typeof quoteSectionInputSchema>
export type QuoteOptionInput = z.infer<typeof quoteOptionInputSchema>

/**
 * A reusable proposal, as `ProposalTemplate.content` stores it.
 *
 * A line may leave `unitPrice` empty and name a catalog item or a service:
 * it is priced when the template is applied, against that client's tier, so a
 * price rise in the catalogue reaches every template quoting it rather than
 * being frozen into a dozen of them. Decision 80.
 */
export const proposalTemplateLineSchema = z.object({
  optionKey: z.string().trim().min(1).max(64).nullish(),
  description: z.string().trim().min(1).max(1000),
  quantity: z.union([z.number(), z.string()]).default('1'),
  unitPrice: z.number().int().nullish(),
  /** The price as typed inc tax, beside `unitPrice`. Ignored when that is empty. */
  unitPriceIncTax: z.number().int().nullish(),
  materialId: cuidSchema.nullish(),
  serviceTypeId: cuidSchema.nullish(),
  taxRateId: cuidSchema.nullish(),
  isOptional: z.boolean().default(false),
})

export const proposalTemplateContentSchema = z.object({
  title: blankToNull(z.string().trim().max(200)),
  termsText: blankToNull(z.string().trim().max(20000)),
  /** How long a proposal raised from this template is valid, in days. */
  validForDays: z.number().int().min(1).max(365).nullish(),
  sections: z.array(quoteSectionInputSchema).max(60).default([]),
  options: z.array(quoteOptionInputSchema).max(6).default([]),
  lines: z.array(proposalTemplateLineSchema).max(200).default([]),
})

export type ProposalTemplateContent = z.infer<typeof proposalTemplateContentSchema>
export type ProposalTemplateLine = z.infer<typeof proposalTemplateLineSchema>

export const proposalTemplateInputSchema = z.object({
  id: cuidSchema.optional(),
  name: z.string().trim().min(1).max(160),
  description: blankToNull(z.string().trim().max(2000)),
  jobCategoryId: cuidSchema.nullish(),
  content: proposalTemplateContentSchema,
})

export type ProposalTemplateInput = z.infer<typeof proposalTemplateInputSchema>

/**
 * The customer's link token. 32 random bytes, base64url — 43 characters. The
 * bounds are loose on purpose; the database lookup is what decides.
 */
const shareTokenSchema = z.string().trim().min(20).max(200)

export const portalTokenSchema = z.object({ token: shareTokenSchema })

/**
 * A drawn signature, as the canvas hands it over: a PNG data URL.
 *
 * Capped at roughly 400 KB of image. A signature drawn on a phone is a few
 * kilobytes; anything near the cap is not a signature.
 */
export const signatureDataUrlSchema = z
  .string()
  .max(560_000, 'That signature is too large.')
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Sign in the box to accept.')

export const portalAcceptSchema = z.object({
  token: shareTokenSchema,
  optionId: cuidSchema.nullish(),
  addOnLineIds: z.array(cuidSchema).max(200).default([]),
  signedName: z.string().trim().min(1, 'Type your name to accept.').max(120),
  signature: signatureDataUrlSchema,
  agreed: z.literal(true, { message: 'Tick the box to agree to the terms.' }),
})

export type PortalAcceptInput = z.infer<typeof portalAcceptSchema>

export const portalDeclineSchema = z.object({
  token: shareTokenSchema,
  category: z.enum(DECLINE_REASON_KEYS),
  note: blankToNull(z.string().trim().max(2000)),
})

export type PortalDeclineInput = z.infer<typeof portalDeclineSchema>

export const invoiceInputSchema = z.object({
  clientId: cuidSchema,
  jobId: cuidSchema.nullish(),
  purchaseOrderNumber: z.string().trim().max(60).nullish(),
  termsText: z.string().trim().max(20000).nullish(),
  notesToCustomer: z.string().trim().max(20000).nullish(),
  discountAmount: z.number().int().min(0).default(0),
  lines: z.array(lineInputSchema).default([]),
})

export const recordPaymentSchema = z.object({
  invoiceId: cuidSchema,
  amount: z.number().int().refine((value) => value !== 0, 'A payment of nothing is not a payment.'),
  method: z.enum(['CARD', 'BANK_TRANSFER', 'CASH', 'CHEQUE', 'DIRECT_DEBIT', 'ACCOUNT_CREDIT', 'OTHER']),
  receivedAt: z.coerce.date().optional(),
  reference: z.string().trim().max(120).nullish(),
  notes: z.string().trim().max(2000).nullish(),
})

export const materialInputSchema = z.object({
  code: z.string().trim().max(60).nullish(),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).nullish(),
  unit: z.string().trim().max(20).default('each'),
  costPrice: z.number().int().min(0).default(0),
  sellPrice: z.number().int().min(0).default(0),
  taxRateId: cuidSchema.nullish(),
  supplierId: cuidSchema.nullish(),
  typeId: cuidSchema.nullish(),
  trackStock: z.boolean().default(false),
})

/**
 * An edit to a catalog item: what is sent changes, what is left out stays.
 *
 * Spelled out rather than `materialInputSchema.partial()`, because zod 4
 * still applies a default under `.partial()` — an edit to the name alone
 * would have reset the price, the cost and the unit.
 */
export const materialUpdateSchema = z.object({
  id: cuidSchema,
  code: z.string().trim().max(60).nullish(),
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(5000).nullish(),
  unit: z.string().trim().max(20).optional(),
  costPrice: z.number().int().min(0).optional(),
  sellPrice: z.number().int().min(0).optional(),
  taxRateId: cuidSchema.nullish(),
  supplierId: cuidSchema.nullish(),
  typeId: cuidSchema.nullish(),
  trackStock: z.boolean().optional(),
})

/** A type the office sorts catalog items into. ADR 022. */
export const materialTypeSchema = z.object({
  id: cuidSchema.optional(),
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).max(999).optional(),
})

/** Up to one page at the largest page size, which is what "select all" ticks. */
const materialIdsSchema = z.array(cuidSchema).min(1).max(200)

export const setMaterialsCuratedSchema = z.object({
  ids: materialIdsSchema,
  curated: z.boolean(),
})

export const setMaterialsTypeSchema = z.object({
  ids: materialIdsSchema,
  typeId: cuidSchema.nullable(),
})

export const reorderMaterialTypesSchema = z.object({
  orderedIds: z.array(cuidSchema).min(1).max(200),
})

export const noteInputSchema = z
  .object({
    body: z.string().trim().min(1).max(20000),
    jobId: cuidSchema.nullish(),
    clientId: cuidSchema.nullish(),
    siteId: cuidSchema.nullish(),
    /**
     * Narrows a job note to one service. Not a fourth parent — the job is still
     * named — so the job card's note list picks a service note up without
     * knowing services exist.
     */
    jobServiceId: cuidSchema.nullish(),
    isPinned: z.boolean().default(false),
  })
  .refine(
    (note) =>
      [note.jobId, note.clientId, note.siteId].filter((value) => value != null).length === 1,
    { message: 'A note attaches to exactly one of a job, client or site.' },
  )
  .refine((note) => note.jobServiceId == null || note.jobId != null, {
    message: 'A note about a service belongs to that service’s job.',
    path: ['jobId'],
  })

/**
 * One answer, in the single shape the filler holds every field type in.
 *
 * Deliberately not a discriminated union per field type. The renderer keeps one
 * `Record<fieldKey, value>` for the whole report, the server already knows each
 * field's type from its definition, and a union here would mean the client
 * having to agree about types it can read out of the definition anyway. `null`
 * is how a field is cleared.
 */
export const reportAnswerSchema = z.union([
  z.string().max(20000),
  z.array(z.string().max(1000)).max(60),
  z.number(),
  z.boolean(),
  z.null(),
])

export const saveAnswersSchema = z.object({
  responseId: cuidSchema,
  /**
   * Only the fields that changed. The filler saves as you go, so a payload is
   * usually one key, and sending the whole report on every keystroke would
   * make a slow connection worse at exactly the wrong moment.
   */
  answers: z.record(z.string().min(1).max(120), reportAnswerSchema).refine(
    (answers) => Object.keys(answers).length <= 200,
    'Too many answers in one save.',
  ),
})

export const reportPhotoSchema = z.object({
  responseId: cuidSchema,
  fieldKey: z.string().min(1).max(120),
  attachmentId: cuidSchema,
  /** The finding. "3 photos from site" is worth nothing without it. */
  caption: z.string().trim().max(500).nullish(),
})

/** Services landing on a job, from the "Add a service" sheet. */
export const addServicesSchema = z.object({
  jobId: cuidSchema,
  serviceTypeIds: z.array(cuidSchema).min(1).max(20),
})

export const jobServiceUpdateSchema = z.object({
  id: cuidSchema,
  scope: z.string().trim().max(5000).nullish(),
  estimatedMinutes: z.number().int().min(0).max(10080).nullish(),
  /** Integer minor units, like every other money field. */
  priceIncTax: z.number().int().min(0).nullish(),
})

export const reorderServicesSchema = z.object({
  jobId: cuidSchema,
  orderedIds: z.array(cuidSchema).min(1).max(50),
})

export const convertInboxMessageSchema = z.object({
  messageId: cuidSchema,
  clientId: cuidSchema.optional(),
  siteId: cuidSchema.optional(),
  /** Omitted when converting to a brand new job. */
  jobId: cuidSchema.optional(),
  title: z.string().trim().max(200).optional(),
})

/**
 * Emailing a quote or an invoice.
 *
 * `to` is optional because the ordinary case is "send it to the client", and
 * making the office retype an address they already hold is how a typo reaches a
 * customer. When it is given it overrides the client's address for this send
 * only — a one-off to a tenant or a strata manager — and is never written back
 * to the client record.
 *
 * The subject and message are optional for the same reason: a default that
 * names the document and the business is right nearly every time.
 */
const addressList = z.array(emailSchema).max(20)

export const sendDocumentSchema = z.object({
  id: cuidSchema,
  to: blankToNull(emailSchema),
  subject: blankToNull(z.string().trim().max(200)),
  message: blankToNull(z.string().trim().max(4000)),
  /** Further recipients. The document still records one primary `to`. */
  cc: addressList.optional(),
  bcc: addressList.optional(),
  /** Files already on the job, sent with the PDF. */
  attachmentIds: z.array(cuidSchema).max(10).optional(),
})

/**
 * A free-form email from the compose window.
 *
 * One shape for a new message and a reply. A quote or invoice does not use
 * this — those go through `sendDocumentSchema`, which also renders the PDF.
 */
export const composeEmailSchema = z.object({
  to: addressList.min(1),
  cc: addressList.optional(),
  bcc: addressList.optional(),
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(20_000),
  attachmentIds: z.array(cuidSchema).max(10).optional(),
  jobId: cuidSchema.nullish(),
  clientId: cuidSchema.nullish(),
  replyToMessageId: cuidSchema.nullish(),
})

export type ComposeEmailInput = z.infer<typeof composeEmailSchema>

export type SendDocumentInput = z.infer<typeof sendDocumentSchema>

/**
 * Recording that a document went out some way other than email.
 *
 * A quote handed across a kitchen bench has reached the customer just as
 * surely as an emailed one, and if the product cannot say so the office is
 * left with a card claiming "not sent" about a job it has already argued
 * about on the phone. So the send history takes paper.
 *
 * `EMAIL` is deliberately not in the list. An email send writes itself, with
 * an `OutboundMessage` and a rendered PDF behind it; letting somebody type one
 * in by hand would produce a row asserting a message that does not exist.
 */
export const markDocumentSentSchema = z.object({
  id: cuidSchema,
  channel: z.enum(['POST', 'HANDED_OVER', 'OTHER']),
  /** Who received it. Free text, because paper does not have an address. */
  toName: blankToNull(z.string().trim().max(160)),
  note: blankToNull(z.string().trim().max(1000)),
})

export type MarkDocumentSentInput = z.infer<typeof markDocumentSentSchema>

/* -------------------------------------------------------------------------- */

/**
 * The service catalogue, as Settings → Catalog edits it.
 *
 * `ServiceType` has been seeded and read-only since it landed; this is the
 * first screen that writes one. Kept to the fields the catalogue card shows —
 * hazards and required licences have their own editors still to build.
 */
export const serviceTypeSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: blankToNull(z.string().trim().max(2000)),
  icon: z.enum(['TERMITE', 'ROACH', 'RODENT', 'INSECT', 'BIRD', 'BEE', 'GENERAL']).default('GENERAL'),
  tone: z.enum(['NEUTRAL', 'BLUE', 'GREEN', 'AMBER', 'RED', 'PURPLE']).default('NEUTRAL'),
  scopeTemplate: blankToNull(z.string().trim().max(5000)),
  estimatedMinutes: z.number().int().min(0).max(10_000).nullish(),
  defaultPriceIncTax: z.number().int().nullish(),
  formId: cuidSchema.nullish(),
  sortOrder: z.number().int().min(0).max(999).optional(),
})

/**
 * One default billing line on a service.
 *
 * `unitPrice` is nullable and that is the interesting part: null means "the
 * catalog price on the day", so a price rise on a `Material` reaches every
 * service quoting it instead of being copied into a dozen rows that then
 * disagree. A free-text line has no material and therefore must carry a price.
 */
export const serviceTypeLineSchema = z
  .object({
    id: cuidSchema.optional(),
    serviceTypeId: cuidSchema,
    materialId: cuidSchema.nullish(),
    description: z.string().trim().min(1).max(1000),
    quantity: z.union([z.number().positive(), z.string()]).default(1),
    unitPrice: z.number().int().nullish(),
    taxRateId: cuidSchema.nullish(),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine((input) => input.materialId != null || input.unitPrice != null, {
    message: 'A line that is not a catalog item needs a price.',
    path: ['unitPrice'],
  })

export const reorderServiceLinesSchema = z.object({
  serviceTypeId: cuidSchema,
  orderedIds: z.array(cuidSchema).min(1).max(200),
})

export type ServiceTypeInput = z.infer<typeof serviceTypeSchema>
export type ServiceTypeLineInput = z.infer<typeof serviceTypeLineSchema>
export type ReorderServiceLinesInput = z.infer<typeof reorderServiceLinesSchema>

/**
 * The office task board.
 *
 * `TASK_LANES` is the order the columns read in, and Done is not among them:
 * a finished task is one with `completedAt`, which is what the job card has
 * always read. Decision 74.
 */
export const TASK_LANES = ['TODO', 'DOING', 'WAITING'] as const
export type TaskLane = (typeof TASK_LANES)[number]

/** The Done column, which is a computed position rather than a stored lane. */
export const TASK_COLUMNS = [...TASK_LANES, 'DONE'] as const
export type TaskColumn = (typeof TASK_COLUMNS)[number]

export const TASK_COLUMN_LABELS: Record<TaskColumn, string> = {
  TODO: 'To do',
  DOING: 'Doing',
  WAITING: 'Waiting',
  DONE: 'Done',
}

export const taskInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: blankToNull(z.string().trim().max(4000)),
  /** Office work has none. A follow-up raised on a job keeps pointing at it. */
  jobId: cuidSchema.nullish(),
  assigneeId: cuidSchema.nullish(),
  dueAt: z.coerce.date().nullish(),
  lane: z.enum(TASK_LANES).default('TODO'),
})

export const taskUpdateSchema = taskInputSchema.partial().extend({ id: cuidSchema })

/**
 * Where a card landed, and the whole column it landed in.
 *
 * `orderedIds` is every task that should end up in `column`, in order, for the
 * same reason `reorderCategories` takes a whole level: a partial list leaves
 * the rows that did not move holding positions among the ones that did.
 *
 * Dropping into `DONE` stamps `completedAt` and dragging out of it clears the
 * stamp, which is the one place the board writes a field it does not store.
 */
export const moveTaskSchema = z.object({
  column: z.enum(TASK_COLUMNS),
  orderedIds: z.array(cuidSchema).min(1).max(500),
})

export type TaskInput = z.infer<typeof taskInputSchema>
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>
export type MoveTaskInput = z.infer<typeof moveTaskSchema>

/**
 * The account's own details, as the settings screen edits them.
 *
 * Partial because the screen saves one card at a time, and a card that does
 * not draw `currency` must not be able to blank it. Empty strings become null
 * rather than surviving as `""`, so "no website" is one value in the database
 * rather than two.
 */
const brandHexSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour such as #8dc63f.')
  .transform((value) => value.toLowerCase())

export const settingsUpdateSchema = z.object({
  businessName: z.string().trim().min(1).max(200).optional(),
  legalName: blankToNull(z.string().trim().max(200)),
  taxNumber: blankToNull(z.string().trim().max(40)),
  email: blankToNull(emailSchema),
  phone: blankToNull(z.string().trim().max(40)),
  website: blankToNull(z.string().trim().max(200)),
  inboundEmailAddress: blankToNull(emailSchema),
  /**
   * The business's own SA Health Pest Controller's Licence.
   *
   * Not a `UserLicence`: it is held by the entity, and every technician works
   * under it. See `_agent/sa-pest-licensing.md`.
   */
  pestControllerLicenceNumber: blankToNull(z.string().trim().max(60)),
  pestControllerLicenceExpiresAt: z.coerce.date().nullish(),
  /**
   * What a printed quote or invoice says.
   *
   * The terms are defaults only: a document captures its own `termsText` when
   * it is created, so editing these never rewrites what a customer already
   * agreed to.
   */
  postalAddress: blankToNull(z.string().trim().max(500)),
  bankAccountName: blankToNull(z.string().trim().max(200)),
  bankBsb: blankToNull(z.string().trim().max(20)),
  bankAccountNumber: blankToNull(z.string().trim().max(40)),
  paymentInstructions: blankToNull(z.string().trim().max(1000)),
  defaultQuoteTerms: blankToNull(z.string().trim().max(5000)),
  defaultInvoiceTerms: blankToNull(z.string().trim().max(5000)),
  documentFooter: blankToNull(z.string().trim().max(500)),
  /**
   * The proposal letterhead's colours. Lower-cased on the way in, because the
   * column's check constraint is. The logo is not here: it is bytes, and
   * `POST /settings/logo` writes it. Decision 81.
   */
  brandColour: blankToNull(brandHexSchema),
  accentColour: blankToNull(brandHexSchema),
  timezone: z.string().trim().min(1).max(64).optional(),
  defaultPaymentTermsDays: z.number().int().min(0).max(365).optional(),
  /**
   * Minutes from midnight, matching the column. The board reads these, so the
   * refinement below is what stops somebody saving a day that ends before it
   * starts and emptying every technician's column.
   */
  workdayStartMinutes: z.number().int().min(0).max(1440).optional(),
  workdayEndMinutes: z.number().int().min(0).max(1440).optional(),
  /** Visits cannot be completed until a photo is on the job. */
  requirePhotosToComplete: z.boolean().optional(),
  /** Recurring jobs. Decision 96. */
  repeatLeadDays: z.number().int().min(0).max(120).optional(),
  repeatAutoBook: z.boolean().optional(),
  repeatAutoCreate: z.boolean().optional(),
  /** Visits at one site on one agreement due this many days apart are one job. 0 is never. */
  repeatCombineDays: z.number().int().min(0).max(90).optional(),
})
  .refine(
    (input) =>
      input.workdayStartMinutes === undefined ||
      input.workdayEndMinutes === undefined ||
      input.workdayStartMinutes < input.workdayEndMinutes,
    { message: 'The working day has to end after it starts.', path: ['workdayEndMinutes'] },
  )

export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>

/* -------------------------------------------------------------------------- */

/**
 * Repeat schedules and service agreements. Decision 96.
 *
 * A calendar day travels as `YYYY-MM-DD`: a visit is due on a day, and an
 * instant would move it across midnight for anyone in another zone.
 */
export const calendarDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date such as 2027-03-18.')

const repeatRuleFields = z.object({
  intervalCount: z.number().int().min(1).max(104),
  intervalUnit: z.enum(['WEEK', 'MONTH']),
  anchor: z.enum(['DUE_DATE', 'COMPLETED_DATE']),
  bookWith: z.enum(['LAST_TECH', 'PERSON', 'UNASSIGNED']),
  bookWithUserId: cuidSchema.nullish(),
  ends: z.enum(['NEVER', 'AFTER_VISITS', 'ON_DATE']),
  endAfterVisits: z.number().int().min(1).max(520).nullish(),
  endsOn: calendarDaySchema.nullish(),
  categoryId: cuidSchema.nullish(),
  estimatedMinutes: z.number().int().min(0).max(10080).nullish(),
  standingNotes: z.string().trim().max(5000).nullish(),
  services: z
    .array(
      z.object({
        serviceTypeId: cuidSchema,
        /** Integer minor units. */
        priceIncTax: z.number().int().min(0).nullish(),
      }),
    )
    .max(20),
})

type RepeatRuleFields = z.infer<typeof repeatRuleFields>

/** What each way of ending and of booking needs, said once for every schema. */
function checkRule(rule: RepeatRuleFields, context: z.core.$RefinementCtx) {
  if (rule.ends === 'AFTER_VISITS' && !rule.endAfterVisits) {
    context.addIssue({ code: 'custom', message: 'Say how many visits.', path: ['endAfterVisits'] })
  }
  if (rule.ends === 'ON_DATE' && !rule.endsOn) {
    context.addIssue({ code: 'custom', message: 'Pick the last date.', path: ['endsOn'] })
  }
  if (rule.bookWith === 'PERSON' && !rule.bookWithUserId) {
    context.addIssue({ code: 'custom', message: 'Pick who to book.', path: ['bookWithUserId'] })
  }
}

/** Starting a schedule from a job: repeat it, or put it in an agreement. */
export const repeatStartSchema = repeatRuleFields
  .extend({
    jobId: cuidSchema,
    /** The imported ServiceM8 chain being taken over, when there is one. */
    legacyRecurrenceId: cuidSchema.nullish(),
    agreementId: cuidSchema.nullish(),
    newAgreement: z
      .object({
        name: z.string().trim().min(1).max(200),
        startsOn: calendarDaySchema,
        endsOn: calendarDaySchema.nullish(),
      })
      .nullish(),
  })
  .superRefine(checkRule)

export type RepeatStartInput = z.infer<typeof repeatStartSchema>

/** Editing a schedule: the whole rule, never a partial one (rule 34). */
export const repeatUpdateSchema = repeatRuleFields
  .extend({ id: cuidSchema, firstDueOn: calendarDaySchema })
  .superRefine(checkRule)

export type RepeatUpdateInput = z.infer<typeof repeatUpdateSchema>

/** A service at one of the client's sites, added from the agreement page. */
export const agreementAddServiceSchema = repeatRuleFields
  .extend({ agreementId: cuidSchema, siteId: cuidSchema, firstDueOn: calendarDaySchema })
  .superRefine(checkRule)

export type AgreementAddServiceInput = z.infer<typeof agreementAddServiceSchema>

/**
 * One service at several of the client's sites at once: the agreement page's
 * service-first way in, and Add sites on a service. One schedule per site,
 * each with the same rule.
 */
export const agreementAddServiceSitesSchema = repeatRuleFields
  .extend({
    agreementId: cuidSchema,
    siteIds: z.array(cuidSchema).min(1, 'Pick a site.').max(100),
    firstDueOn: calendarDaySchema,
  })
  .superRefine(checkRule)

export type AgreementAddServiceSitesInput = z.infer<typeof agreementAddServiceSitesSchema>

/**
 * Editing one of the agreement's services from its page: what is done, what
 * it costs, how often and the instructions for every visit. The rest of the
 * rule (booking, ending) is the schedule's own and stays as it is.
 */
export const agreementServiceUpdateSchema = z.object({
  agreementId: cuidSchema,
  scheduleId: cuidSchema,
  intervalCount: repeatRuleFields.shape.intervalCount,
  intervalUnit: repeatRuleFields.shape.intervalUnit,
  firstDueOn: calendarDaySchema,
  services: repeatRuleFields.shape.services.min(1, 'Pick a service.'),
  /** The instructions written into every job it makes. Left out, they stay as they are. */
  standingNotes: repeatRuleFields.shape.standingNotes,
})

export type AgreementServiceUpdateInput = z.infer<typeof agreementServiceUpdateSchema>

/**
 * Editing one service at every site it is done at (the By service view):
 * the services, how often and the instructions, the same for each. Each site
 * keeps its own first visit, and its own price for a service whose price is
 * left out.
 */
export const agreementServicesUpdateSchema = z.object({
  agreementId: cuidSchema,
  scheduleIds: z.array(cuidSchema).min(1).max(100),
  intervalCount: repeatRuleFields.shape.intervalCount,
  intervalUnit: repeatRuleFields.shape.intervalUnit,
  services: z
    .array(
      z.object({
        serviceTypeId: cuidSchema,
        /** Integer minor units, the same at every site. Left out, each site keeps its own. */
        priceIncTax: z.number().int().min(0).nullish(),
      }),
    )
    .min(1, 'Pick a service.')
    .max(20),
  /** Left out, they stay as they are. */
  standingNotes: repeatRuleFields.shape.standingNotes,
})

export type AgreementServicesUpdateInput = z.infer<typeof agreementServicesUpdateSchema>

/** How an agreement is meant to be billed. Recorded only; nothing invoices from it yet. */
export const agreementBillingSchema = z.enum(['MONTHLY', 'PER_VISIT', 'UPFRONT'])

export type AgreementBilling = z.infer<typeof agreementBillingSchema>

export const agreementInputSchema = z
  .object({
    clientId: cuidSchema,
    name: z.string().trim().min(1).max(200),
    startsOn: calendarDaySchema,
    endsOn: calendarDaySchema.nullish(),
    notes: z.string().trim().max(5000).nullish(),
    billing: agreementBillingSchema.optional(),
    autoRenew: z.boolean().optional(),
  })
  .refine((input) => !input.endsOn || input.endsOn >= input.startsOn, {
    message: 'The term has to end after it starts.',
    path: ['endsOn'],
  })

export const agreementUpdateSchema = z
  .object({
    id: cuidSchema,
    name: z.string().trim().min(1).max(200).optional(),
    startsOn: calendarDaySchema.optional(),
    endsOn: calendarDaySchema.nullish(),
    status: z.enum(['ACTIVE', 'PAUSED', 'ENDED']).optional(),
    notes: z.string().trim().max(5000).nullish(),
    billing: agreementBillingSchema.optional(),
    autoRenew: z.boolean().optional(),
  })
  .refine((input) => !input.endsOn || !input.startsOn || input.endsOn >= input.startsOn, {
    message: 'The term has to end after it starts.',
    path: ['endsOn'],
  })

/** One service at one site in the builder: each runs on its own schedule. */
const agreementBuildServiceSchema = z.object({
  serviceTypeId: cuidSchema,
  intervalCount: z.number().int().min(1).max(104),
  intervalUnit: z.enum(['WEEK', 'MONTH']),
  firstDueOn: calendarDaySchema,
  /** Integer minor units, per visit. */
  priceIncTax: z.number().int().min(0).nullish(),
  /** This service's first visit is the job the builder was opened from. */
  fromJob: z.boolean().optional(),
})

/**
 * The agreement builder's Create: a new agreement, or more services on an
 * existing one, with every site's services, in one transaction.
 */
export const agreementBuildSchema = z
  .object({
    agreement: z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('new'),
        clientId: cuidSchema,
        name: z.string().trim().min(1).max(200),
        startsOn: calendarDaySchema,
        endsOn: calendarDaySchema.nullish(),
        billing: agreementBillingSchema,
        autoRenew: z.boolean(),
      }),
      z.object({ kind: z.literal('existing'), id: cuidSchema }),
    ]),
    /** The job the builder was opened from, when it was. */
    jobId: cuidSchema.nullish(),
    sites: z
      .array(
        z.object({
          siteId: cuidSchema,
          services: z.array(agreementBuildServiceSchema).min(1, 'Every site needs a service.').max(20),
        }),
      )
      .min(1, 'Pick at least one site.')
      .max(100),
  })
  .superRefine((input, context) => {
    if (
      input.agreement.kind === 'new' &&
      input.agreement.endsOn &&
      input.agreement.endsOn < input.agreement.startsOn
    ) {
      context.addIssue({ code: 'custom', message: 'The term has to end after it starts.', path: ['agreement', 'endsOn'] })
    }
    const sites = new Set<string>()
    let fromJob = 0
    input.sites.forEach((site, index) => {
      if (sites.has(site.siteId)) {
        context.addIssue({ code: 'custom', message: 'That site is in twice.', path: ['sites', index] })
      }
      sites.add(site.siteId)
      const services = new Set<string>()
      for (const service of site.services) {
        if (services.has(service.serviceTypeId)) {
          context.addIssue({ code: 'custom', message: 'That service is at the site twice.', path: ['sites', index] })
        }
        services.add(service.serviceTypeId)
        if (service.fromJob) fromJob += 1
      }
    })
    if (fromJob > 1 || (fromJob === 1 && !input.jobId)) {
      context.addIssue({ code: 'custom', message: 'Only the job’s own service can start from the job.', path: ['jobId'] })
    }
  })

export type AgreementBuildInput = z.infer<typeof agreementBuildSchema>

/* -------------------------------------------------------------------------- */

/** A hazard that comes with a kind of work, read before going in. */
export const serviceHazardSchema = z.object({
  title: z.string().trim().min(1).max(80),
  control: z.string().trim().min(1).max(1000),
  severity: z.enum(['CAUTION', 'CRITICAL']).default('CAUTION'),
})

/**
 * The licence catalogue, as edited in Settings.
 *
 * `expires: false` is an internal sign-off — dated, and it does not lapse. The
 * refinement below is what stops a type being marked as expected of everybody
 * while also being unable to expire, which would make every technician read as
 * permanently held and the gap report useless.
 */
export const licenceTypeSchema = z.object({
  name: z.string().trim().min(1).max(160),
  authority: blankToNull(z.string().trim().max(120)),
  expires: z.boolean().default(true),
  warnDays: z.number().int().min(0).max(365).default(60),
  expectedOfFieldStaff: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(999).optional(),
})

/**
 * One person's holding of one ticket.
 *
 * `expiresAt` is nullable on purpose even for a licence that expires: a record
 * with the number typed in and the date still to find is a real state, and
 * `licenceStanding` reports it as `undated` rather than pretending it is fine.
 */
export const userLicenceSchema = z
  .object({
    userId: cuidSchema,
    licenceTypeId: cuidSchema,
    reference: blankToNull(z.string().trim().max(80)),
    issuedAt: z.coerce.date().nullish(),
    expiresAt: z.coerce.date().nullish(),
    attachmentId: cuidSchema.nullish(),
  })
  .refine(
    (input) => !input.issuedAt || !input.expiresAt || input.issuedAt <= input.expiresAt,
    { message: 'A licence cannot expire before it was issued.', path: ['expiresAt'] },
  )

/**
 * The technician card, written whole.
 *
 * `licenceTypeIds` is every endorsement switched on, not a change: the router
 * writes the card's number, expiry and photo to each and lets go of the rest,
 * so the rows standing for one card cannot disagree. `attachmentId` left out
 * keeps the photo already on file.
 */
export const technicianCardSchema = z.object({
  userId: cuidSchema,
  licenceTypeIds: z.array(cuidSchema).max(40),
  reference: blankToNull(z.string().trim().max(80)),
  expiresAt: z.coerce.date().nullish(),
  attachmentId: cuidSchema.nullish(),
})

export type LicenceTypeInput = z.infer<typeof licenceTypeSchema>
export type UserLicenceInput = z.infer<typeof userLicenceSchema>
export type TechnicianCardInput = z.infer<typeof technicianCardSchema>
export type JobCategoryInput = z.infer<typeof jobCategorySchema>
export type ReorderCategoriesInput = z.infer<typeof reorderCategoriesSchema>

export type LoginInput = z.infer<typeof loginSchema>
export type JobInput = z.infer<typeof jobInputSchema>
export type CreateJobInput = z.infer<typeof createJobSchema>
export type JobTemplateInput = z.infer<typeof jobTemplateSchema>
export type SiteKnowledgeInput = z.infer<typeof siteKnowledgeSchema>
export type ClientInput = z.infer<typeof clientInputSchema>
export type ClientCreateInput = z.infer<typeof clientCreateSchema>
export type ContactInput = z.infer<typeof contactInputSchema>
export type VisitInput = z.infer<typeof visitInputSchema>
export type MoveVisitInput = z.infer<typeof moveVisitSchema>
export type SplitVisitInput = z.infer<typeof splitVisitSchema>
export type MoveVisitsInput = z.infer<typeof moveVisitsSchema>
export type LeaveInput = z.infer<typeof leaveInputSchema>
export type LeaveUpdateInput = z.infer<typeof leaveUpdateSchema>
export type CalendarEventInput = z.infer<typeof calendarEventInputSchema>
export type CalendarEventUpdate = z.infer<typeof calendarEventUpdateSchema>
export type MoveEventInput = z.infer<typeof moveEventSchema>
export type LineInput = z.infer<typeof lineInputSchema>
export type ReportAnswerInput = z.infer<typeof reportAnswerSchema>
export type SaveAnswersInput = z.infer<typeof saveAnswersSchema>
export type JobServiceUpdateInput = z.infer<typeof jobServiceUpdateSchema>
